import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, Smartphone, User, Mail, MapPin } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { toast } from 'sonner'

import { useAuth } from '@/context/auth-context'

export default function SignupPage() {
    const navigate = useNavigate()
    const { signup } = useAuth()
    const [step, setStep] = useState(1) // 1: Mobile Input, 2: OTP Verify, 3: Profile Details
    const [isLoading, setIsLoading] = useState(false)

    // Data Form
    const [mobile, setMobile] = useState('')
    const [otp, setOtp] = useState('')
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        neetAttempt: 'Fresher',
        targetYear: '2025',
        city: '',
        state: ''
    })

    const handleSendOtp = (e: React.FormEvent) => {
        e.preventDefault()
        if (!mobile || mobile.length < 10) return

        setIsLoading(true)
        setTimeout(() => {
            setIsLoading(false)
            setStep(2)
            toast.success('OTP sent successfully!')
        }, 1000)
    }

    const handleVerifyOtp = (e: React.FormEvent) => {
        e.preventDefault()
        if (!otp || otp.length < 4) return

        setIsLoading(true)
        setTimeout(() => {
            setIsLoading(false)
            if (otp.length === 4) {
                setStep(3)
                toast.success('Mobile verified!')
            } else {
                toast.error('Invalid OTP')
            }
        }, 1000)
    }

    const handleCompleteProfile = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formData.name) return

        setIsLoading(true)
        try {
            await signup({
                ...formData,
                role: 'student'
            })
            toast.success('Account created successfully!')
            navigate('/dashboard')
        } catch (error) {
            console.error(error)
            toast.error('Signup Failed')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Elements */}


            <GlassmorphicCard className="w-full max-w-lg p-8 relative z-10">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-display font-bold text-theme-primary mb-2">Create Account</h1>
                    <p className="text-theme-secondary">Join India's best NEET preparation platform</p>
                </div>

                {/* Progress Steps */}
                <div className="flex justify-between items-center mb-8 px-4">
                    <div className={`flex flex-col items-center gap-2 ${step >= 1 ? 'text-primary-blue' : 'text-slate-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${step >= 1 ? 'border-primary-blue bg-primary-blue/10' : 'border-slate-300'}`}>1</div>
                        <span className="text-xs">Mobile</span>
                    </div>
                    <div className={`h-[2px] w-full mx-2 ${step >= 2 ? 'bg-primary-blue' : 'bg-slate-300'}`} />
                    <div className={`flex flex-col items-center gap-2 ${step >= 2 ? 'text-primary-blue' : 'text-slate-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${step >= 2 ? 'border-primary-blue bg-primary-blue/10' : 'border-slate-300'}`}>2</div>
                        <span className="text-xs">Verify</span>
                    </div>
                    <div className={`h-[2px] w-full mx-2 ${step >= 3 ? 'bg-primary-blue' : 'bg-slate-300'}`} />
                    <div className={`flex flex-col items-center gap-2 ${step >= 3 ? 'text-primary-blue' : 'text-slate-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${step >= 3 ? 'border-primary-blue bg-primary-blue/10' : 'border-slate-300'}`}>3</div>
                        <span className="text-xs">Profile</span>
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    {step === 1 && (
                        <motion.form
                            key="step1"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            onSubmit={handleSendOtp}
                            className="space-y-6"
                        >
                            <div className="space-y-4">
                                <button type="button" className="w-full py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-3">
                                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" fill="#FBBC05" />
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                    </svg>
                                    Sign up with Google
                                </button>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white dark:bg-slate-900 text-slate-500">Or with mobile</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-theme-secondary">Mobile Number</label>
                                    <div className="relative">
                                        <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <input
                                            type="tel"
                                            value={mobile}
                                            onChange={(e) => setMobile(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue"
                                            placeholder="Enter your 10 digit mobile number"
                                            maxLength={10}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || mobile.length < 10}
                                className="w-full py-3 bg-primary-blue hover:bg-blue-600 text-white font-bold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2"
                            >
                                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Continue'}
                            </button>
                        </motion.form>
                    )}

                    {step === 2 && (
                        <motion.form
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            onSubmit={handleVerifyOtp}
                            className="space-y-6"
                        >
                            <div className="text-center">
                                <p className="text-theme-secondary mb-2">Code sent to +91 {mobile}</p>
                                <button type="button" onClick={() => setStep(1)} className="text-sm text-primary-blue hover:underline">
                                    Change number
                                </button>
                            </div>

                            <div className="space-y-2">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        className="w-full px-4 py-4 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 text-center font-mono text-2xl tracking-[0.5em] focus:outline-none focus:border-primary-blue"
                                        placeholder="----"
                                        maxLength={4}
                                        autoFocus
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || otp.length < 4}
                                className="w-full py-3 bg-primary-blue hover:bg-blue-600 text-white font-bold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2"
                            >
                                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify & Continue'}
                            </button>
                        </motion.form>
                    )}

                    {step === 3 && (
                        <motion.form
                            key="step3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            onSubmit={handleCompleteProfile}
                            className="space-y-4"
                        >
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary">Full Name</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue"
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary">Email (Optional)</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue"
                                        placeholder="Enter your email"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-theme-secondary">NEET Attempt</label>
                                    <select
                                        value={formData.neetAttempt}
                                        onChange={(e) => setFormData({ ...formData, neetAttempt: e.target.value })}
                                        className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary focus:outline-none focus:border-primary-blue"
                                    >
                                        <option>Fresher</option>
                                        <option>Dropper</option>
                                        <option>2nd Dropper</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-theme-secondary">Target Year</label>
                                    <select
                                        value={formData.targetYear}
                                        onChange={(e) => setFormData({ ...formData, targetYear: e.target.value })}
                                        className="w-full px-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary focus:outline-none focus:border-primary-blue"
                                    >
                                        <option>2025</option>
                                        <option>2026</option>
                                        <option>2027</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-theme-secondary">City/State</label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue"
                                        placeholder="e.g. Mumbai, Maharashtra"
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 bg-primary-blue hover:bg-blue-600 text-white font-bold rounded-lg glow-primary transition-all duration-300 flex items-center justify-center gap-2 mt-2"
                            >
                                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Complete Signup'}
                            </button>
                        </motion.form>
                    )}
                </AnimatePresence>

                <div className="mt-8 text-center text-sm">
                    <span className="text-theme-secondary">Already have an account? </span>
                    <Link to="/login" className="font-semibold text-primary-blue hover:text-blue-600 transition-colors">
                        Login
                    </Link>
                </div>
            </GlassmorphicCard>
        </div>
    )
}
